


















































































































































































































































import { Component, Vue } from 'vue-property-decorator'
import ApiClient from '@/services/api-client'
import moment from 'moment'
import {
  PrepAutoCheckOutcome,
  PrepTask,
  PrepTaskStatus,
  ProductionPrep,
  ProductionPrepArea,
  ProductionPrepAreaProgress,
  ProductionPrepTask
} from '@/models'
import { Utils } from '@/utils'
import {
  AREA_PROGRESS,
  AREA_PROGRESS_ICONS,
  PREP_AUTO_CHECK_OUTCOMES,
  PREP_TASK_STATUS,
  PREP_TASK_STATUSES,
  PrepStateView,
  RESOLVED_PREP_TASK_STATUSES,
  areaProgress,
  autoCheckResultText,
  confirmPrep,
  countedTasks,
  isAreaOpenByDefault,
  newPrepTask,
  percent,
  prepCountedTasks,
  prepStateView,
  prepTaskStatusOption,
  prepTasks,
  removePrepItem,
  resolveRequestText,
  savePrepItem,
  showPrepError,
  showPrepModal,
  taskProgress
} from '@/utils/production-prep'
import { downloadPrepReport } from '@/utils/production-prep-report'
import { DATE_TIME_FORMAT } from '@/utils/constants'
import UpsertProductionPrepModal from '@/components/modals/UpsertProductionPrepModal.vue'
import UpsertPrepAreaModal from '@/components/modals/UpsertPrepAreaModal.vue'
import UpsertPrepTaskModal from '@/components/modals/UpsertPrepTaskModal.vue'
import ProductionPrepTaskLogModal from '@/components/modals/ProductionPrepTaskLogModal.vue'
import PrepAreaCard from '@/components/productionPrep/PrepAreaCard.vue'
import PrepProgressHeader from '@/components/productionPrep/PrepProgressHeader.vue'
import PrepTaskBadges from '@/components/productionPrep/PrepTaskBadges.vue'

interface AreaStatus {
  progress: ProductionPrepAreaProgress;
  icon: string;
  variant: string;
  label: string;
  percent: number;
}

@Component({
  components: {
    UpsertProductionPrepModal,
    UpsertPrepAreaModal,
    UpsertPrepTaskModal,
    ProductionPrepTaskLogModal,
    PrepAreaCard,
    PrepProgressHeader,
    PrepTaskBadges
  }
})
export default class ProductionPrepDetail extends Vue {
  private prep: ProductionPrep | null = null
  private loading = false
  // Status chosen in the header counters: only its tasks are shown
  private statusFilter: PrepTaskStatus | null = null
  // Status and notes saves of each task, sent one after the other so they arrive in order
  private taskSaves: Record<number, Promise<void>> = {}
  private loadFailed = false
  private generatingReport = false
  // Keys of the automatic checks the backend can run, and the tasks being checked now
  private autoCheckKeys: string[] = []
  private checkingIds: number[] = []
  // Status and notes saves started for each task, to know if one was changed while the checks ran
  private savesStarted: Record<number, number> = {}
  private noteDrafts: { [taskId: number]: string } = {}
  private editingNoteId: number | null = null
  private openRequests: number[] = []
  // Areas opened or closed by hand; the rest are open while they have something to do
  private areaToggles: { [areaId: number]: boolean } = {}
  private selectedArea: ProductionPrepArea | null = null
  private selectedTask: ProductionPrepTask | null = null
  private statuses = PREP_TASK_STATUSES
  private STATUS = PREP_TASK_STATUS
  private AREA_PROGRESS = AREA_PROGRESS

  get prepId(): number {
    return Number(this.$route.params.id)
  }

  private get allTasks(): ProductionPrepTask[] {
    return this.prep ? prepTasks(this.prep) : []
  }

  get counters() {
    // Same tasks as the areas; not applicable tasks leave the total, only the done ones count as progress
    const { applicable, done } = taskProgress(this.prep ? prepCountedTasks(this.prep) : [])
    return { required: applicable, resolved: done }
  }

  get state(): PrepStateView {
    return prepStateView(this, this.counters.required, this.counters.resolved)
  }

  get visibleAreas(): ProductionPrepArea[] {
    if (!this.prep) return []
    return this.statusFilter ? this.prep.areas.filter(area => this.visibleTasks(area).length) : this.prep.areas
  }

  /** Tasks in each status, the ones the status filter shows */
  get statusCounts(): { [status in PrepTaskStatus]: number } {
    return PREP_TASK_STATUSES.reduce((counts, option) => ({
      ...counts,
      [option.value]: this.allTasks.filter(task => task.status === option.value).length
    }), {} as { [status in PrepTaskStatus]: number })
  }

  toggleFilter(status: PrepTaskStatus) {
    this.statusFilter = this.statusFilter === status ? null : status
  }

  /** Same rule as the calendar: the required tasks count, or every task when none is required */
  areaStatus(area: ProductionPrepArea): AreaStatus {
    const { applicable, done } = taskProgress(countedTasks(area))
    const progress = areaProgress(applicable, done)
    return {
      progress,
      ...AREA_PROGRESS_ICONS[progress],
      label: progress === AREA_PROGRESS.NOT_APPLICABLE ? this.$t('production_prep.status.NOT_APPLICABLE').toString() : `${done}/${applicable}`,
      percent: percent(done, applicable)
    }
  }

  isAreaOpen(area: ProductionPrepArea): boolean {
    if (this.statusFilter) return true
    if (area.id in this.areaToggles) return this.areaToggles[area.id]
    return isAreaOpenByDefault(this.areaStatus(area).progress)
  }

  toggleArea(area: ProductionPrepArea) {
    this.$set(this.areaToggles, area.id, !this.isAreaOpen(area))
  }

  async created() {
    await Promise.all([this.loadPrep(), this.loadAutoCheckKeys()])
    // Every time the preparation is opened its checks look again, without making the page wait
    this.runAutoChecks()
  }

  private async loadAutoCheckKeys() {
    try {
      this.autoCheckKeys = await ApiClient.getPrepAutoChecks()
    } catch (error) {
      showPrepError(this, error)
    }
  }

  /** The task has a check this app can run */
  hasAutoCheck(task: ProductionPrepTask): boolean {
    return !!task.autoCheck && this.autoCheckKeys.includes(task.autoCheck)
  }

  autoCheckOutcome(task: ProductionPrepTask) {
    return PREP_AUTO_CHECK_OUTCOMES[task.autoCheckResult as PrepAutoCheckOutcome]
  }

  autoCheckText(task: ProductionPrepTask): string {
    return autoCheckResultText(this, task)
  }

  formatDateTime(date?: string | null): string {
    return date ? moment(date).format(DATE_TIME_FORMAT) : ''
  }

  /**
   * Checks every task that has a check. A task changed by hand while they ran keeps that change:
   * its result would be older than what the person just did
   */
  private async runAutoChecks() {
    if (!this.prep) return
    const tasks = this.allTasks.filter(task => this.hasAutoCheck(task))
    if (!tasks.length) return
    await Promise.all(tasks.map(task => this.taskSaves[task.id]))
    const savesBefore = { ...this.savesStarted }
    this.checkingIds = tasks.map(task => task.id)
    try {
      const checked = await ApiClient.runProductionPrepAutoChecks(this.prep.id)
      checked
        .filter(task => this.savesStarted[task.id] === savesBefore[task.id])
        .forEach(task => this.replaceTask(task))
    } catch (error) {
      showPrepError(this, error)
    } finally {
      this.checkingIds = []
    }
  }

  /** The check button of a task, in the queue of its saves like a status change */
  runTaskAutoCheck(task: ProductionPrepTask): Promise<void> {
    const check = (this.taskSaves[task.id] || Promise.resolve()).then(() => this.sendAutoCheck(task))
    this.taskSaves[task.id] = check
    return check
  }

  private async sendAutoCheck(task: ProductionPrepTask) {
    if (!this.prep) return
    this.checkingIds.push(task.id)
    try {
      this.replaceTask(await ApiClient.runProductionPrepTaskAutoCheck(this.prep.id, task.id))
    } catch (error) {
      showPrepError(this, error)
    } finally {
      this.checkingIds = this.checkingIds.filter(id => id !== task.id)
    }
  }

  async loadPrep() {
    try {
      this.loading = true
      this.loadFailed = false
      this.prep = await ApiClient.getProductionPrep(this.prepId)
      this.noteDrafts = this.allTasks.reduce((drafts, task) => ({ ...drafts, [task.id]: task.notes || '' }), {})
    } catch (error) {
      this.loadFailed = !this.prep
      showPrepError(this, error)
    } finally {
      this.loading = false
    }
  }

  isResolved(task: ProductionPrepTask): boolean {
    return RESOLVED_PREP_TASK_STATUSES.includes(task.status)
  }

  visibleTasks(area: ProductionPrepArea): ProductionPrepTask[] {
    return this.statusFilter ? area.tasks.filter(task => task.status === this.statusFilter) : area.tasks
  }

  statusOption(status: PrepTaskStatus) {
    return prepTaskStatusOption(status)
  }

  requestText(task: ProductionPrepTask): string {
    return this.prep ? resolveRequestText(task.requestText, this.prep) : ''
  }

  toggleRequest(task: ProductionPrepTask) {
    const index = this.openRequests.indexOf(task.id)
    if (index >= 0) this.openRequests.splice(index, 1)
    else this.openRequests.push(task.id)
  }

  async copyRequest(task: ProductionPrepTask) {
    if (!await Utils.copyClipboard(this.requestText(task))) {
      showPrepError(this, null, this.$t('production_prep.detail.copy_failed').toString())
      return
    }
    this.$bvToast.toast(this.$t('production_prep.detail.request_copied').toString(), {
      title: this.$t('feedback.feedback_message_success_title').toString(),
      variant: 'success',
      solid: true,
      autoHideDelay: 2000
    })
  }

  /** The circle of the task: done, or back to pending */
  async toggleDone(task: ProductionPrepTask) {
    await this.updateStatus(task, task.status === PREP_TASK_STATUS.DONE ? PREP_TASK_STATUS.PENDING : PREP_TASK_STATUS.DONE)
  }

  editNote(task: ProductionPrepTask) {
    this.editingNoteId = task.id
  }

  async setStatus(task: ProductionPrepTask, status: PrepTaskStatus) {
    if (task.status === status) return
    await this.updateStatus(task, status)
  }

  async saveNotes(task: ProductionPrepTask) {
    this.editingNoteId = null
    const notes = (this.noteDrafts[task.id] || '').trim()
    if (notes === (task.notes || '')) return
    await this.updateStatus(task, task.status)
  }

  private updateStatus(task: ProductionPrepTask, status: PrepTaskStatus): Promise<void> {
    this.$set(this.savesStarted, task.id, (this.savesStarted[task.id] || 0) + 1)
    const save = (this.taskSaves[task.id] || Promise.resolve()).then(() => this.sendStatus(task, status))
    this.taskSaves[task.id] = save
    return save
  }

  private async sendStatus(task: ProductionPrepTask, status: PrepTaskStatus) {
    if (!this.prep) return
    try {
      const updated = await ApiClient.updateProductionPrepTaskStatus(this.prep.id, task.id, status, this.noteDrafts[task.id])
      this.replaceTask(updated)
      if (this.editingNoteId !== task.id) this.noteDrafts[task.id] = updated.notes || ''
    } catch (error) {
      showPrepError(this, error)
    }
  }

  private replaceTask(updated: ProductionPrepTask) {
    if (!this.prep) return
    this.prep.areas.forEach(area => {
      const index = area.tasks.findIndex(task => task.id === updated.id)
      if (index >= 0) area.tasks.splice(index, 1, updated)
    })
  }

  /** Every task of the area is not applicable */
  isAreaNotApplicable(area: ProductionPrepArea): boolean {
    return area.tasks.every(task => task.status === PREP_TASK_STATUS.NOT_APPLICABLE)
  }

  /** All the tasks of the area not applicable, or all of them pending again; the notes are kept */
  async toggleAreaNotApplicable(area: ProductionPrepArea) {
    if (!this.prep) return
    const toPending = this.isAreaNotApplicable(area)
    const status = toPending ? PREP_TASK_STATUS.PENDING : PREP_TASK_STATUS.NOT_APPLICABLE
    const action = toPending ? 'area_pending' : 'area_not_applicable'
    const confirmed = await confirmPrep(
      this,
      this.$tc(`production_prep.detail.confirm_${action}`, area.tasks.length, { name: area.name, count: area.tasks.length }).toString(),
      this.$t('production_prep.detail.area_status_title').toString(),
      this.$t(`production_prep.detail.${action}`).toString(),
      'primary'
    )
    if (!confirmed) return
    try {
      // The saves of its tasks still on their way go first, so none of them overwrites the area change
      await Promise.all(area.tasks.map(task => this.taskSaves[task.id]))
      const updated = await ApiClient.updateProductionPrepAreaStatus(this.prep.id, area.id, status)
      updated.tasks.forEach(task => {
        this.replaceTask(task)
        this.noteDrafts[task.id] = task.notes || ''
      })
      // The area opens or closes again by its new progress
      this.$delete(this.areaToggles, area.id)
    } catch (error) {
      showPrepError(this, error)
    }
  }

  async downloadReport() {
    if (!this.prep) return
    try {
      this.generatingReport = true
      await downloadPrepReport(this, this.prep)
    } catch (error) {
      // Not a backend error: the technical message (a chunk that didn't load...) says nothing to the user
      console.error(error)
      showPrepError(this, null, this.$t('production_prep.report.failed').toString())
    } finally {
      this.generatingReport = false
    }
  }

  openPrepModal() {
    this.$bvModal.show('UpsertProductionPrepModal')
  }

  savePrep(prep: ProductionPrep, done: (saved: boolean) => void) {
    return savePrepItem(this, () => ApiClient.saveProductionPrep(prep), done, this.loadPrep)
  }

  openAreaModal(area: ProductionPrepArea | null) {
    this.selectedArea = area
    showPrepModal(this, 'UpsertPrepAreaModal')
  }

  saveArea(area: ProductionPrepArea, done: (saved: boolean) => void) {
    const prepId = this.prepId
    return savePrepItem(this, () => ApiClient.saveProductionPrepArea(prepId, area), done, this.loadPrep)
  }

  openTaskModal(task: ProductionPrepTask | null, areaId: number) {
    this.selectedTask = task || { ...newPrepTask(areaId), status: PREP_TASK_STATUS.PENDING }
    showPrepModal(this, 'UpsertPrepTaskModal')
  }

  openLogModal(task: ProductionPrepTask) {
    this.selectedTask = task
    showPrepModal(this, 'ProductionPrepTaskLogModal')
  }

  saveTask(task: PrepTask, done: (saved: boolean) => void) {
    const prepId = this.prepId
    return savePrepItem(this, () => ApiClient.saveProductionPrepTask(prepId, task as ProductionPrepTask), done, this.loadPrep)
  }

  confirmDeleteArea(area: ProductionPrepArea) {
    const prepId = this.prepId
    const message = this.$t('production_prep.detail.confirm_delete_area', { name: area.name }).toString()
    return removePrepItem(this, message, () => ApiClient.deleteProductionPrepArea(prepId, area.id), this.loadPrep)
  }

  confirmDeleteTask(task: ProductionPrepTask) {
    const prepId = this.prepId
    const message = this.$t('production_prep.detail.confirm_delete_task', { name: task.name }).toString()
    return removePrepItem(this, message, () => ApiClient.deleteProductionPrepTask(prepId, task.id), this.loadPrep)
  }
}
